import { useState, useEffect } from 'react';
import {
    useLocation,
    useNavigate,
} from 'react-router-dom';
import styled from 'styled-components';
import { Card } from "@mui/material";
import { useSnackbar } from "notistack";

import { useAdminUserHook } from '../../Hooks/AdminUserControl/adminUserControlHooks';
import { useAuthenticationHook } from '../../Hooks/Authentication/authenticationHooks';

import { AdminUserControl, AdminUserAddress } from '../../Models/AdminUserControl/AdminUserControl';
import { Authentication } from "../../Models/Authentication/Authentication";

import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import RequiredFieldHelper from '../../Utilities/Helpers/RequiredFieldHelper';
import RequiredParametersHelper from '../../Utilities/Helpers/RequiredParametersHelper';
import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { AdminUserField, AdminUserAddressField, AdminUserProfileField, ImageFileField, AuthenticationField, RolesField } from '../../Utilities/Enums/ParametersEnum';
import { ActiveStatusEnum } from '../../Utilities/Enums/StatusEnums';
import { CurrencyEnum } from '../../Utilities/Enums/CurrencyEnums';

import { ErrorMessage } from '../../Constants/Messages';

import AdminUserDetailsForm from './AdminUserDetailsForm';
import AdminUserAccountInformation from './AdminUserAccountInformation';
import AdminUserSecurity from './AdminUserSecurity';
import CustomDeleteConfirmation from '../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import QRCodeModal from './QRCodeModal';

const DetailsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: none;
    border-radius: 0px !important;
    display: grid;
    grid-template-columns: auto;
    gap: 30px;
    box-shadow: none !important;
    padding: 10px 0px;
`;

export default function AdminUserDetails() {
    // Variables
    let navigate = useNavigate();
    let location = useLocation();
    let _userData: Authentication = LocalStorageHelper.getUserData();
    let queryParams = new URLSearchParams(location.search);
    let routePath: any = location?.pathname ?? RouteEnum.ADMIN_USER_DETAILS;

    let adminuserid = queryParams.get('id') ?? "0";
    let view = parseInt(queryParams.get('view') ?? ViewType.PREVIEW.toString());
    let requiredFields = RequiredParametersHelper.adminUserControl();
    let resetPasswordRequiredFields = RequiredParametersHelper.resetPassword();
    const { enqueueSnackbar } = useSnackbar();

    // Use States
    const [queryStr, setQueryStr] = useState({
        pageSize: 10,
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        id: adminuserid,
    });
    let [viewType, setviewType] = useState(view ?? ViewType.ADD);
    let [openDelete, setopenDelete] = useState(false);
    let [toDeleteItem, settoDeleteItem] = useState<any>(null);
    let [formData, setformData] = useState<AdminUserControl>(new AdminUserControl());
    let [previewData, setpreviewData] = useState<AdminUserControl>(new AdminUserControl());
    let [errorMessages, setErrorMessages]: any = useState({});
    let [resetPassword, setresetPassword] = useState(false);
    let [openQRCodeModal, setopenQRCodeModal] = useState(false);
    
    const q_queryKey = [CachingEnum.COMPANIES, queryStr]
    const { DropdownData, dropdownFetchStatus, getAdminUserById, getAdminUserProfileImageById, createAdminUser, editAdminUserById, editAdminUserProfileById, resetPasswordAdminUserById, uploadProfileAdminUserById, deleteAdminUserById } = useAdminUserHook(q_queryKey, queryStr, false);
    const detailsCommonProps = {
        adminuserid: adminuserid,
        viewType: viewType,
        setviewType: setviewType,
        setopenDelete: setopenDelete,
        settoDeleteItem: settoDeleteItem,
        previewData: previewData,
        formData: formData,
        setformData: setformData,
        errorMessages: errorMessages,
        setErrorMessages: setErrorMessages,
        DropdownData: DropdownData,
        dropdownFetchStatus: dropdownFetchStatus,
        resetPassword: resetPassword,
        setresetPassword: setresetPassword,
        routePath: routePath,
        openQRCodeModal: openQRCodeModal,
        setopenQRCodeModal: setopenQRCodeModal,
        handleSubmit: async () => {
            let _requiredFields = viewType != ViewType.ADD && !resetPassword ? requiredFields?.filter((field: any) => field.keyName != AdminUserField.PASSWORD && field.keyName != AdminUserField.CONFIRM_PASSWORD) : requiredFields;
            let _validateForm = RequiredFieldHelper.validateForm(_requiredFields, formData, errorMessages, setErrorMessages, enqueueSnackbar);

            if (_validateForm?.hasError) { return true; }

            if(_requiredFields?.find((field: any) => field.keyName == AdminUserField.PASSWORD)?.keyName != null && formData?.[AdminUserField.PASSWORD] != formData?.[AdminUserField.CONFIRM_PASSWORD]) {
                enqueueSnackbar(ErrorMessage.PASSWORD_AND_CONFIRM_PASSSWORD_NOT_MATCH, { variant: 'error' });
                return true;
            }

            let _success: boolean = false;
            let publicId = "0";
            let _newFormData = {...new AdminUserControl()};
            // let proceedReinitialized = false;

            if(!formData?.[AdminUserField.PUBLIC_ID]) {
                let createResponse = await createAdminUser(JSON.stringify(formData));

                _success = createResponse?.isSuccess ?? false;
                publicId = createResponse?.data?.data?.[AdminUserField.PUBLIC_ID] ?? '0';
                _newFormData = {..._newFormData, ...createResponse?.data?.data};
            } else {
                let editResponse = (routePath == RouteEnum.ADMIN_USER_DETAILS ?
                    await editAdminUserById(formData?.[AdminUserField.PUBLIC_ID], JSON.stringify(formData)) : await editAdminUserProfileById(formData?.[AdminUserField.PUBLIC_ID], JSON.stringify(formData))
                );

                _success = editResponse?.isSuccess ?? false;
                publicId = editResponse?.data?.data?.[AdminUserField.PUBLIC_ID] ?? '0';
                _newFormData = {..._newFormData, ...editResponse?.data?.data};
            }

            if(_success) {
                if(formData?.[ImageFileField.IMAGE_FILE]) {
                    let _profImageForm: any = new FormData();
                    _profImageForm.append("File", formData?.[ImageFileField.IMAGE_FILE]);

                    let uploadResponse = await uploadProfileAdminUserById(formData?.[AdminUserField.PUBLIC_ID], _profImageForm);   
                }
                // navigate(`${RouteEnum.ADMIN_USER_DETAILS}?id=${publicId}&view=${ViewType.PREVIEW}`, { replace: true });
                navigate(`${routePath}?id=${publicId}&view=${ViewType.PREVIEW}`, { replace: true });
                

                if(setviewType) {
                    setviewType(ViewType.PREVIEW);

                    getAdminUserData(_userData?.[AuthenticationField.PUBLIC_ID] == publicId);
                }

                setformData({..._newFormData});
                setpreviewData({..._newFormData});
            }
        },
        handleResetPassword: async () => {
            let resetPasswordFormData: any = {
                [AdminUserField.NEW_PASSWORD]: formData?.[AdminUserField.PASSWORD],
                [AdminUserField.CONFIRM_PASSWORD]: formData?.[AdminUserField.CONFIRM_PASSWORD],
            };
            let _validateResetPasswordForm = RequiredFieldHelper.validateForm(resetPasswordRequiredFields, formData, errorMessages, setErrorMessages, enqueueSnackbar);

            if (_validateResetPasswordForm?.hasError) { return true; }

            if(resetPasswordRequiredFields?.find((field: any) => field.keyName == AdminUserField.PASSWORD)?.keyName != null && resetPasswordFormData?.[AdminUserField.NEW_PASSWORD] != resetPasswordFormData?.[AdminUserField.CONFIRM_PASSWORD]) {
                enqueueSnackbar(ErrorMessage.PASSWORD_AND_CONFIRM_PASSSWORD_NOT_MATCH, { variant: 'error' });
                return true;
            }

            let resetPasswordResponse = await resetPasswordAdminUserById(formData?.[AdminUserField.PUBLIC_ID], JSON.stringify(resetPasswordFormData));
            let _success: boolean = resetPasswordResponse?.isSuccess ?? false;

            if(_success) {
                if(setviewType) {
                    setviewType(ViewType.PREVIEW);
                    setresetPassword(false);
                    setformData({...previewData});
                }
            }
        },
    };

    //Event Handlers
    const getAdminUserData = async (updateLocalStorageUserData: boolean = false) => {
        if(adminuserid != '0') {
            let getDetails = await getAdminUserById(adminuserid);
            let getAdminUserProfile: any = await getAdminUserProfileImageById(adminuserid);
            let _formData = {...formData};

            if(getDetails?.data) {
                _formData = {
                    ...formData,
                    ...getDetails.data,
                    [AdminUserField.ROLE_PUBLIC_ID]: getDetails?.data?.[AdminUserField.ROLE]?.[RolesField.PUBLIC_ID] ?? null,
                    [AdminUserField.STATUS]: getDetails?.data?.[AdminUserField.STATUS] != ActiveStatusEnum.ALL ? getDetails?.data?.[AdminUserField.STATUS] : ActiveStatusEnum.INACTIVE,
                    [AdminUserField.PROFILE]: {
                        ...getDetails?.data?.[AdminUserField.PROFILE],
                        [AdminUserProfileField.COMPANY_PUBLIC_ID]: (getDetails?.data?.[AdminUserField.PROFILE]?.[AdminUserProfileField.COMPANY_PUBLIC_ID] != null && getDetails?.data?.[AdminUserField.PROFILE]?.[AdminUserProfileField.COMPANY_PUBLIC_ID] != "" ? getDetails?.data?.[AdminUserField.PROFILE]?.[AdminUserProfileField.COMPANY_PUBLIC_ID] : null),
                        [AdminUserProfileField.VENDOR_IDS]: getDetails?.data?.[AdminUserField.PROFILE]?.[AdminUserProfileField.VENDORS]?.map((item: any) => { return item?.publicId }) ?? [],
                        // [AdminUserProfileField.CURRENCY_CODE]: getDetails?.data?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CURRENCY_CODE] != null && getDetails?.data?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CURRENCY_CODE] != "" ? getDetails?.data?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CURRENCY_CODE] : CurrencyEnum.PHP,
                    },
                    [AdminUserField.ADDRESS]: getDetails?.data?.[AdminUserField.ADDRESS] ?? new AdminUserAddress(),
                    [ImageFileField.VIEW_IMAGE]: getAdminUserProfile?.data ?? null,
                };

                if(updateLocalStorageUserData) {
                    LocalStorageHelper.saveUpdateUserProfile(true);
                }
            }

            setformData(_formData);
            setpreviewData(_formData);
        }
    }
    
    const handleCloseDeleteConfirmation = async () => {
        setopenDelete(false);
        settoDeleteItem(null);
    }

    useEffect(() => {
            getAdminUserData();
    }, [adminuserid]);
    
    //Rendering Functions
    const renderAdminUserDetails = () => {
        return <AdminUserDetailsForm {...detailsCommonProps} />
    }

    const renderAccountInformation = () => {
        return <AdminUserAccountInformation {...detailsCommonProps} />
    }

    const renderAdminUserSecurity = () => {
        return <AdminUserSecurity {...detailsCommonProps} />
    }

    return <DetailsContainer>
        {renderAdminUserDetails()}
        {renderAccountInformation()}
        {viewType != ViewType.ADD && renderAdminUserSecurity()}

        {openDelete && <CustomDeleteConfirmation
            openDialog={openDelete}
            handleClose={handleCloseDeleteConfirmation}
            ItemToDelete={toDeleteItem?.[AdminUserField.PROFILE]?.[AdminUserProfileField.NAME] ?? null} // Optional to display name of the item to be deleted
            showRemarksField={true}
            deleteItem={async (remarks: any) => {
                if(toDeleteItem?.[AdminUserField.PUBLIC_ID]) {
                    let deleteReponse: any = await deleteAdminUserById(toDeleteItem?.[AdminUserField.PUBLIC_ID], remarks);

                    if(deleteReponse?.isSuccess) {
                        handleCloseDeleteConfirmation();
                        
                        navigate(RouteEnum.ADMIN_USER_CONTROL, { replace: true });
                    }
                }
            }} />
        }

        {openQRCodeModal && <QRCodeModal
            openModal={openQRCodeModal}
            setopenModal={setopenQRCodeModal}
            formData={formData}
            getAdminUserData={getAdminUserData} />}
    </DetailsContainer>
}
